import type { SelfDescribingJson } from '@snowplow/browser-tracker'
import {
  trackPageView,
  trackSelfDescribingEvent,
} from '@snowplow/browser-tracker'

/**
 * Leaf helpers for firing Snowplow events.
 * Kept out of analytics.ts / snowplow/events.ts so events can fire without a cycle.
 * Context/client-event builders are registered from events.ts at module load.
 */

type SnowplowEvent = {
  context?: Array<SelfDescribingJson> | null
  options?: {
    itemsToSkip?: Array<string>
    contextsToSkip?: Array<string>
  }
  itemContextSource?: string
  title?: string
  event?: SelfDescribingJson
}

type AsyncBaseContext = (
  itemContextSource?: string,
  options?: SnowplowEvent['options'],
) => Promise<Array<SelfDescribingJson<Record<string, unknown>>> | null | undefined>

type ClientEventBuilder = (params: any) => SnowplowEvent

let asyncBaseContextImpl: AsyncBaseContext = async() => []
let clientEventImpl: ClientEventBuilder = () => {
  throw new Error('clientEvent builder has not been registered')
}

export function registerSnowplowTrackHelpers(helpers: {
  asyncBaseContext: AsyncBaseContext
  clientEvent: ClientEventBuilder
}) {
  asyncBaseContextImpl = helpers.asyncBaseContext
  clientEventImpl = helpers.clientEvent
}

const mergeAsyncContext = (
  context: Array<SelfDescribingJson<Record<string, unknown>>> | null | undefined,
  asyncContext: Array<SelfDescribingJson<Record<string, unknown>>> | null | undefined,
) => {
  const newContext = context || []
  newContext.push(...(asyncContext || []))
  return newContext
}

export async function trackClientEvent(eventData: any) {
  trackEvent(clientEventImpl(eventData))
}

export async function trackEvent(passedEvent: SnowplowEvent | undefined | Promise<SnowplowEvent | undefined>) {
  const isPromise = passedEvent instanceof Promise
  const event = isPromise ? (await passedEvent) : passedEvent

  if (!event) {
    return
  }

  try {
    const asyncContextResult = await asyncBaseContextImpl(event.itemContextSource, event.options)
    event.context = mergeAsyncContext(event?.context, asyncContextResult)
    if ('title' in event && event.title) {
      trackPageView(event as any)
    } else if ('event' in event && event.event) {
      trackSelfDescribingEvent(event as any)
    }
  } catch(error) {
    console.error(error)
  }
}
